import type { ComponentPropsWithRef, ElementType, ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * Card surfaces.
 *
 * - `default` white card, 1px `--border-default`, radius 10 — the workhorse
 * - `dark`    `--ink-800`, no border, radius 10 — signal card, "NIET GESTELD", month card
 * - `accent`  `--accent-tint-3` with `--accent-border` — suggestion card, current lifecycle step
 * - `warn`    `--warn-tint` with `--warn-border` — planning advice bar
 * - `muted`   `--surface-subtle` with a dashed border — future lifecycle step
 *
 * Never a shadow inside the app. Shadows are only for floating layers (Drawer,
 * the public report page).
 */
export type CardVariant = 'default' | 'dark' | 'accent' | 'warn' | 'muted';

/** `none` when the card contains a `CardHeader` or edge-to-edge rows. */
export type CardPadding = 'none' | 'md' | 'lg';

const variantStyles: Record<CardVariant, string> = {
    default:
        'bg-[var(--surface-card)] border border-[var(--border-default)] text-[var(--text-primary)]',
    dark: 'bg-[var(--ink-800)] border-0 text-[var(--text-on-dark)]',
    accent: 'bg-[var(--accent-tint-3)] border border-[var(--accent-border)] text-[var(--text-primary)]',
    warn: 'bg-[var(--warn-tint)] border border-[var(--warn-border)] text-[var(--warn-ink)]',
    muted: 'bg-[var(--surface-subtle)] border border-dashed border-[var(--border-placeholder)] text-[var(--text-primary)]',
};

const paddingStyles: Record<CardPadding, string> = {
    none: '',
    md: 'p-4',
    lg: 'p-[18px]',
};

export type CardProps = ComponentPropsWithRef<'div'> & {
    variant?: CardVariant;
    padding?: CardPadding;
    /** radius 10 by default; 9 for cost/finding/lifecycle cards, 12 for the public page. */
    radius?: 9 | 10 | 12;
    /** Clip children to the radius — needed when rows or a table run to the edge. */
    clip?: boolean;
    as?: ElementType;
};

export function Card({
    variant = 'default',
    padding = 'none',
    radius = 10,
    clip = false,
    as,
    className,
    children,
    ...rest
}: CardProps) {
    const Tag = (as ?? 'div') as ElementType;

    return (
        <Tag
            className={cn(
                'font-[family-name:var(--font-body)]',
                radius === 9
                    ? 'rounded-[9px]'
                    : radius === 12
                      ? 'rounded-[12px]'
                      : 'rounded-[10px]',
                clip && 'overflow-hidden',
                variantStyles[variant],
                paddingStyles[padding],
                className,
            )}
            {...rest}
        >
            {children}
        </Tag>
    );
}

export type CardHeaderProps = Omit<ComponentPropsWithRef<'div'>, 'title'> & {
    /** Space Grotesk 600, 15px (17px when `size="lg"`). */
    title: ReactNode;
    /** Small grey line under or next to the title. */
    description?: ReactNode;
    /** Right-hand slot: a `ghost` Button, a count, a `StatusPill`. */
    action?: ReactNode;
    size?: 'md' | 'lg';
    /** Set `false` inside a padded card where the rule would be wrong. */
    divider?: boolean;
    /** Dark cards flip the text colours. */
    onDark?: boolean;
};

/**
 * Card header: padding 14px 16px, 1px `--border-subtle` rule underneath.
 * Title colour is always explicit — it never inherits from the card.
 */
export function CardHeader({
    title,
    description,
    action,
    size = 'md',
    divider = true,
    onDark = false,
    className,
    ...rest
}: CardHeaderProps) {
    return (
        <div
            className={cn(
                'flex flex-wrap items-center justify-between gap-3 px-4 py-[14px]',
                divider && 'border-b border-[var(--border-subtle)]',
                className,
            )}
            {...rest}
        >
            <div className="min-w-0">
                <div
                    className={cn(
                        'font-[family-name:var(--font-display)] font-semibold leading-[1.3]',
                        size === 'lg' ? 'text-[17px]' : 'text-[15px]',
                        onDark ? 'text-[var(--text-on-dark)]' : 'text-[var(--text-primary)]',
                    )}
                >
                    {title}
                </div>
                {description ? (
                    <div
                        className={cn(
                            'mt-1 text-[12px] leading-[1.4]',
                            onDark
                                ? 'text-[var(--text-on-dark-4-safe)]'
                                : 'text-[var(--text-tertiary-safe)]',
                        )}
                    >
                        {description}
                    </div>
                ) : null}
            </div>
            {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
        </div>
    );
}
